import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import { useErrorMessages } from '@/hooks/i18n';
import { extractAndFormatPercentage } from '@/shared/utils/fieldEditor';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFieldEditorContext } from '../../context/FieldEditorContext';
import FieldEditorContentWrapper from '../FieldEditorContentWrapper';

const PercentageContent = () => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { currentField, handleUpdateFieldAnswer, mainQueryRefetch, handleClose } = useFieldEditorContext();
  const { control, reset, watch, setValue, handleSubmit } = useForm({ defaultValues: { fieldValue: '', commentary: '' } });

  const [isEditMode, setIsEditMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!currentField) return;

    if (currentField?.value !== null || currentField?.commentary !== null) {
      setIsEditMode(true);
      reset({
        fieldValue: currentField?.value || '',
        commentary: currentField?.commentary || ''
      });
      return;
    }
    if (currentField.AI?.answers[0]) {
      setIsEditMode(false);
      reset({
        fieldValue: extractAndFormatPercentage(currentField.AI?.answers[0]) || '',
        commentary: currentField.AI.answers[0]
      });
      return;
    }
  }, [currentField]);

  const saveValue = async (data: { fieldValue: string; commentary: string }) => {
    try {
      setError(null);
      setLoading(true);

      await handleUpdateFieldAnswer({
        id: currentField.fieldAnswerId,
        recordId: currentField.recordId,
        value: isEditMode ? data.fieldValue : null,
        commentary: isEditMode ? data.commentary : null
      });

      await mainQueryRefetch();
      handleClose();
    } catch (error) {
      setError({ title: 'Error', message: defaultErrorMessage });
    } finally {
      setLoading(false);
    }
  };

  const editSuggested = (suggestion: string) => {
    setValue('fieldValue', extractAndFormatPercentage(suggestion));
    setValue('commentary', suggestion);
    setIsEditMode(true);
  };

  const clearSuggestion = () => {
    setValue('fieldValue', currentField?.AI?.answers?.length ? '' : null);
    setValue('commentary', currentField?.AI?.answers?.length ? '' : null);
    setIsEditMode(true);
  };

  const startWithAISuggestion = () => {
    setValue('fieldValue', extractAndFormatPercentage(currentField.AI?.answers[0]) || '');
    setValue('commentary', currentField.AI?.answers[0]);
    setIsEditMode(false);
  };

  const revertPriorDraft = () => {
    if (currentField?.value || currentField?.commentary) {
      setValue('fieldValue', currentField?.value);
      setValue('commentary', currentField?.commentary);
      setIsEditMode(true);
    }
  };

  if (!currentField) return <></>;

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <FieldEditorContentWrapper
        isEditMode={isEditMode}
        showRevertPriorDraft={
          (Boolean(currentField?.value) && currentField?.value !== watch('fieldValue')) ||
          (Boolean(currentField?.commentary) && currentField?.commentary !== watch('commentary'))
        }
        error={error}
        confirmButton={{ name: 'Save', onClick: handleSubmit(saveValue) }}
        startWithAISuggestion={startWithAISuggestion}
        editSuggested={editSuggested}
        clearSuggestion={clearSuggestion}
        revertPriorDraft={revertPriorDraft}
      >
        <Controller
          name="fieldValue"
          control={control}
          rules={{
            validate: (value) => /^(0*([0-9]*(\.[0-9]*)?|\.[0-9]+))$/.test(value) || !value || 'Please enter a valid percentage'
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              label={currentField.name}
              value={value || ''}
              onChange={(e) => onChange(e)}
              styleType={isEditMode ? 'default' : 'magicWand'}
              size="md"
              errorText={error?.message}
              autoFocus
            />
          )}
        />
        <Controller
          name="commentary"
          control={control}
          rules={{
            validate: (value) => !isEditMode || !value || value?.length <= 250 || 'Maximum number of characters exceeded'
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Textarea
              value={isEditMode ? value || '' : currentField.AI?.answers[0]}
              onChange={(e) => onChange(e.target.value)}
              label="Commentary"
              styleType={isEditMode ? 'default' : 'magicWand'}
              size="md"
              errorText={error?.message}
              limitCharactersTo={isEditMode ? 250 : 0}
            />
          )}
        />
      </FieldEditorContentWrapper>
    </>
  );
};

export default PercentageContent;
